import React from "react";
import { useTranslation } from "react-i18next";

import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import { Box, Button, Divider, Grid2 as Grid, IconButton, InputAdornment, TextField, Typography } from "@mui/material";
import { DesktopDatePicker } from "@mui/x-date-pickers";
import { Select } from "components/forms";
import dayjs from "dayjs";
import { FormikProps } from "formik";
import { useDateFormatSelect } from "store";
import { FacilityFee, FrequencyType, PercentageOf } from "store/types/deal/debt";
import toIsoMidday from "utils/to-iso-midday";

import { FormValues } from "./schemas";

interface Props {
  formik: FormikProps<FormValues>;
}

export const FacilityTermsFeesSection: React.FC<Props> = ({ formik }) => {
  const { t } = useTranslation("debt");
  const dateFormat = useDateFormatSelect();

  const getFeeError = (index: number, field: keyof FacilityFee) => {
    const feeErrors = formik.errors.fees?.[index];
    if (feeErrors && typeof feeErrors !== "string") {
      return feeErrors[field];
    }
    return undefined;
  };

  const isFeeTouched = (index: number, field: keyof FacilityFee) => {
    const feeTouched = formik.touched.fees?.[index];
    if (feeTouched && typeof feeTouched !== "boolean") {
      return feeTouched[field];
    }
    return false;
  };

  const handleAddFee = () => {
    const newFee: FacilityFee & { key: string } = {
      id: 0,
      key: Date.now().toString(),
      type: "custom_one_time",
      start_date: null,
      end_date: null,
      frequency: "month",
      amount: 0,
      percentage_of: null
    };
    formik.setFieldValue("fees", [...formik.values.fees, newFee]);
  };

  const handleRemoveFee = (index: number) => {
    const updatedFees = formik.values.fees.filter((_, i) => i !== index);
    formik.setFieldValue("fees", updatedFees);
  };

  const handleFeeChange = (index: number, field: keyof FacilityFee, value: FacilityFee[keyof FacilityFee]) => {
    const updatedFees = [...formik.values.fees];
    updatedFees[index] = { ...updatedFees[index], [field]: value };
    formik.setFieldValue("fees", updatedFees);
  };

  return (
    <Box>
      <Box sx={{ mb: 5 }}>
        <Typography variant="subtitle1" sx={{ mb: 1 }}>
          {t("feesSection.title")}
        </Typography>
        <Divider />
      </Box>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        {formik.values.fees.map((fee, index) => (
          <Box key={fee.key || fee.id} sx={{ position: "relative" }}>
            <Grid container spacing={5} alignItems="center">
              <Grid size={3}>
                <Select
                  name={`fees.${index}.type`}
                  label=""
                  variant="standard"
                  value={fee.type}
                  items={[
                    { key: "credit_facility", value: "credit_facility", text: t("feesSection.creditFacilityFee") },
                    { key: "arrangement", value: "arrangement", text: t("feesSection.arrangementFee") },
                    { key: "custom_one_time", value: "custom_one_time", text: t("feesSection.oneTimeFee") }
                  ]}
                  onChange={(e) => handleFeeChange(index, "type", e.target.value as FacilityFee["type"])}
                  sx={{
                    fontWeight: 600,
                    "&:before": { display: "none" },
                    "&:after": { display: "none" },
                    "& .MuiSelect-select": {
                      paddingTop: 0,
                      paddingBottom: 0,
                      fontWeight: 600
                    }
                  }}
                />
              </Grid>

              {fee.type === "credit_facility" ? (
                <>
                  <Grid size={1.5}>
                    <DesktopDatePicker
                      format={dateFormat}
                      views={["year", "month", "day"]}
                      label={t("feesSection.startDate")}
                      value={fee.start_date ? dayjs(fee.start_date) : null}
                      onChange={(newValue) =>
                        handleFeeChange(index, "start_date", newValue ? toIsoMidday(newValue) : null)
                      }
                      sx={{ width: "100%" }}
                      slotProps={{
                        textField: {
                          error: isFeeTouched(index, "start_date") && Boolean(getFeeError(index, "start_date")),
                          helperText: isFeeTouched(index, "start_date") ? getFeeError(index, "start_date") : undefined
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={1.5}>
                    <DesktopDatePicker
                      format={dateFormat}
                      views={["year", "month", "day"]}
                      label={t("feesSection.endDate")}
                      value={fee.end_date ? dayjs(fee.end_date) : null}
                      onChange={(newValue) =>
                        handleFeeChange(index, "end_date", newValue ? toIsoMidday(newValue) : null)
                      }
                      sx={{ width: "100%" }}
                      slotProps={{
                        textField: {
                          error: isFeeTouched(index, "end_date") && Boolean(getFeeError(index, "end_date")),
                          helperText: isFeeTouched(index, "end_date") ? getFeeError(index, "end_date") : undefined
                        }
                      }}
                    />
                  </Grid>
                  <Grid size={1.5}>
                    <Select
                      name={`fees.${index}.frequency`}
                      label={t("feesSection.frequency")}
                      variant="outlined"
                      fullWidth
                      value={fee.frequency}
                      items={[
                        { key: "month", value: "month", text: t("common.monthly") },
                        { key: "quarter", value: "quarter", text: t("common.quarterly") },
                        { key: "semi-annual", value: "semi-annual", text: t("common.semiAnnually") },
                        { key: "year", value: "year", text: t("common.annually") }
                      ]}
                      error={isFeeTouched(index, "frequency") && Boolean(getFeeError(index, "frequency"))}
                      helperText={isFeeTouched(index, "frequency") ? getFeeError(index, "frequency") : undefined}
                      onChange={(e) => handleFeeChange(index, "frequency", e.target.value as FrequencyType)}
                    />
                  </Grid>
                </>
              ) : (
                <Grid size={3}>
                  <DesktopDatePicker
                    format={dateFormat}
                    views={["year", "month", "day"]}
                    label={t("feesSection.feeDate")}
                    value={fee.start_date ? dayjs(fee.start_date) : null}
                    onChange={(newValue) =>
                      handleFeeChange(index, "start_date", newValue ? toIsoMidday(newValue) : null)
                    }
                    sx={{ width: "100%" }}
                    slotProps={{
                      textField: {
                        error: isFeeTouched(index, "start_date") && Boolean(getFeeError(index, "start_date")),
                        helperText: isFeeTouched(index, "start_date") ? getFeeError(index, "start_date") : undefined
                      }
                    }}
                  />
                </Grid>
              )}

              <Grid size={3}>
                <Select
                  name={`fees.${index}.percentage_of`}
                  label={t("feesSection.type")}
                  variant="outlined"
                  fullWidth
                  value={fee.percentage_of ?? "fixed"}
                  items={[
                    { key: "fixed", value: "fixed", text: t("feesSection.fixedAmount") },
                    { key: "total_commitment", value: "total_commitment", text: t("feesSection.percentageFromCommitment") },
                    {
                      key: "unfunded_commitment",
                      value: "unfunded_commitment",
                      text: t("feesSection.percentageFromUnfundedCommitment")
                    },
                    {
                      key: "funded_amount",
                      value: "funded_amount",
                      text: t("feesSection.percentageFromFundedAmount")
                    }
                  ]}
                  onChange={(e) =>
                    handleFeeChange(
                      index,
                      "percentage_of",
                      e.target.value === "fixed" ? null : (e.target.value as PercentageOf)
                    )
                  }
                />
              </Grid>

              <Grid size={fee.type === "credit_facility" ? 1.1 : 2.6}>
                <TextField
                  fullWidth
                  type="number"
                  label={fee.percentage_of ? t("feesSection.feeRate") : t("feesSection.amount")}
                  value={fee.amount}
                  onChange={(e) => handleFeeChange(index, "amount", parseFloat(e.target.value) || 0)}
                  error={isFeeTouched(index, "amount") && Boolean(getFeeError(index, "amount"))}
                  helperText={isFeeTouched(index, "amount") ? getFeeError(index, "amount") : undefined}
                  slotProps={{
                    input: {
                      endAdornment: fee.percentage_of ? <InputAdornment position="end">%</InputAdornment> : undefined
                    }
                  }}
                />
              </Grid>
            </Grid>

            <IconButton
              onClick={() => handleRemoveFee(index)}
              color="error"
              sx={{ position: "absolute", right: 0, top: "50%", transform: "translateY(-50%)" }}
            >
              <DeleteIcon />
            </IconButton>
          </Box>
        ))}
      </Box>

      <Button variant="outlined" startIcon={<AddIcon />} onClick={handleAddFee} sx={{ mt: 2 }}>
        {t("feesSection.addFee")}
      </Button>
    </Box>
  );
};
